// components/workflow/FlowAbout.tsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Paper,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Button,
  Divider,
  useTheme,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  EmojiEvents as EmojiEventsIcon,
  Shield as ShieldIcon,
  ThumbUp as ThumbUpIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Restaurant as RestaurantIcon,
  CleaningServices as CleaningServicesIcon,
  Groups as GroupsIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  description: string;
  experience: string;
  image?: string;
  specialty: string[];
}

interface ValueProposition {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const FlowAbout: React.FC = () => {
  const { t } = useTranslation();
  const fAbout = (key: string) => t(`workflowabout:${key}`);

  const theme = useTheme();

  // Team members data
  // const teamMembers: TeamMember[] = [
  //   {
  //     id: 'mark-jensen',
  //     name: t('teamMember1Name', 'Mark Jensen'),
  //     role: t('teamMember1Role', 'Operations Director'),
  //     description: t('teamMember1Description', 'Over 15 years experience in facility operations and hygiene management. Expert in workflow optimization.'),
  //     experience: '15+ years',
  //     specialty: ['Workflow Optimization', 'Hygiene Compliance', 'Operations Management']
  //   },
  //   {
  //     id: 'anna-karlsson',
  //     name: t('teamMember2Name', 'Anna Karlsson'),
  //     role: t('teamMember2Role', 'Hygiene Specialist'),
  //     description: t('teamMember2Description', 'Certified hygiene auditor with expertise in food safety regulations and compliance standards.'),
  //     experience: '12+ years',
  //     specialty: ['Food Safety', 'Compliance Audits', 'Training Programs']
  //   },
  //   {
  //     id: 'thomas-berg',
  //     name: t('teamMember3Name', 'Thomas Berg'),
  //     role: t('teamMember3Role', 'Dishroom Operations Manager'),
  //     description: t('teamMember3Description', 'Specialist in commercial dishwashing systems and chemical management with 12 years experience.'),
  //     experience: '12+ years',
  //     specialty: ['Dishwashing Systems', 'Chemical Management', 'Equipment Maintenance']
  //   },
  //   {
  //     id: 'sofia-mikkelsen',
  //     name: t('teamMember4Name', 'Sofia Mikkelsen'),
  //     role: t('teamMember4Role', 'Cleaning Systems Expert'),
  //     description: t('teamMember4Description', 'Focuses on facility cleaning protocols, equipment, and staff training for optimal hygiene.'),
  //     experience: '10+ years',
  //     specialty: ['Cleaning Protocols', 'Equipment Training', 'Hygiene Standards']
  //   }
  // ];

  // Value propositions
  const valuePropositions: ValueProposition[] = [
    {
      id: 'expertise',
      icon: <EmojiEventsIcon />,
      title: fAbout('value1Title'),
      description: fAbout('value1Description'),
      color: '#2196f3'
    },
    {
      id: 'efficiency',
      icon: <TimelineIcon />,
      title: fAbout('value2Title'),
      description: fAbout('value2Description'),
      color: '#4caf50'
    },
    {
      id: 'compliance',
      icon: <ShieldIcon />,
      title: fAbout('value3Title'),
      description: fAbout('value3Description'),
      color: '#ff9800'
    },
    {
      id: 'flexibility',
      icon: <ThumbUpIcon />,
      title: fAbout('value4Title'),
      description: fAbout('value4Description'),
      color: '#9c27b0'
    }
  ];

  // Services highlight
  const servicesHighlight = [
    {
      id: 'waiter-support',
      icon: <RestaurantIcon />,
      title: fAbout('service1Title'),
      description: fAbout('service1Description')
    },
    {
      id: 'kitchen-helper',
      icon: <GroupsIcon />,
      title: fAbout('service2Title'),
      description: fAbout('service2Description')
    },
    {
      id: 'commercial-cleaning',
      icon: <CleaningServicesIcon />,
      title: fAbout('service3Title'),
      description: fAbout('service3Description')
    },
    {
      id: 'logistics',
      icon: <LocalShippingIcon />,
      title: fAbout('service4Title'),
      description: fAbout('service4Description')
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3,
              textAlign: 'center'
            }}
          >
            {fAbout('aboutHeroTitle')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              maxWidth: 800,
              mx: 'auto',
              textAlign: 'center',
              opacity: 0.9,
              lineHeight: 1.6,
              fontWeight: 300
            }}
          >
            {fAbout('aboutHeroDescription')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 10 }}>
        {/* Mission & Vision */}
        <Grid container spacing={6} sx={{ mb: 10 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderTop: `4px solid ${theme.palette.primary.main}`,
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BusinessIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {fAbout('missionTitle')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                {fAbout('missionDescription')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                borderTop: `4px solid ${theme.palette.secondary.main}`,
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EmojiEventsIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {fAbout('visionTitle')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                {fAbout('visionDescription')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* What Makes Us Different */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {fAbout('differentTitle')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: 800,
              mx: 'auto'
            }}
          >
            {fAbout('differentSubtitle')}
          </Typography>

          <Grid container spacing={4}>
            {valuePropositions.map((value) => (
              <Grid item xs={12} sm={6} md={3} key={value.id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: `${value.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <Box sx={{ color: value.color, fontSize: 32 }}>
                        {value.icon}
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Our Services */}
        <Box sx={{ mb: 10 }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {t('servicesTitle', 'Our Core Services')}
          </Typography>

          <Grid container spacing={4}>
            {servicesHighlight.map((service) => (
              <Grid item xs={12} sm={6} md={3} key={service.id}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Our Team */}
        {/* <Box sx={{ mb: 10 }}> */}
        {/* <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 6,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {t('teamTitle', 'Our Leadership Team')}
          </Typography>

          <Grid container spacing={4}>
            {teamMembers.map((member) => (
              <Grid item xs={12} sm={6} md={3} key={member.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          width: 70,
                          height: 70,
                          bgcolor: theme.palette.primary.main,
                          mr: 2
                        }}
                      >
                        {member.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {member.name}
                        </Typography>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                          {member.role}
                        </Typography>
                        <Chip
                          label={member.experience}
                          size="small"
                          sx={{ mt: 1 }}
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 3 }}>
                      {member.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Specialties:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {member.specialty.map((spec) => (
                        <Chip
                          key={spec}
                          label={spec}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid> */}
        {/* </Box> */}

        {/* Our Approach */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 6 },
            mb: 10,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {fAbout('approachTitle')}
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                step: 1,
                title: fAbout('step1Title'),
                description: fAbout('step1Description')
              },
              {
                step: 2,
                title: fAbout('step2Title'),
                description: fAbout('step2Description')
              },
              {
                step: 3,
                title: fAbout('step3Title'),
                description: fAbout('step3Description')
              },
              {
                step: 4,
                title: fAbout('step4Title'),
                description: fAbout('step4Description')
              }
            ].map((step) => (
              <Grid item xs={12} md={3} key={step.step}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {step.step}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Contact CTA */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {fAbout('ctaTitle')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
              color: 'text.secondary',
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            {fAbout('ctaDescription')}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              href="/workflow/contact"
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600
              }}
            >
              {fAbout('ctaButton')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              href="/workflow/booking"
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 600
              }}
            >
              {fAbout('ctaButton2')}
            </Button>
          </Stack>

          <Divider sx={{ my: 4 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                {/* <PhoneIcon color="primary" /> */}
                <Box>
                  <PhoneIcon color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    +45 42 22 84 26
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>

                <Box>
                  <EmailIcon color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    workflow@nordisksupport.com
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>

                <Box>
                  <LocationOnIcon color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Copenhagen, Denmark
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default FlowAbout;